import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { User } from "lucide-react";
import { ChatMessage } from "./types";
import { cn } from "@/lib/utils";
import { CopilotIcon } from "./CopilotIcon";

interface ChatMessagesProps {
  messages: ChatMessage[];
  isLoading?: boolean;
}

export function ChatMessages({ messages, isLoading }: ChatMessagesProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Inline SVG for the gradient background
  const backgroundStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='486' height='912' viewBox='0 0 486 912' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg filter='url(%23filter0_f_272_28578)'%3E%3Cellipse cx='332.045' cy='303.498' rx='250.894' ry='151.824' transform='rotate(-38.2171 332.045 303.498)' fill='%234AFFDD' fill-opacity='0.17'/%3E%3C/g%3E%3Cg filter='url(%23filter1_f_272_28578)'%3E%3Cellipse cx='88.3961' cy='382.141' rx='381.766' ry='92.8688' transform='rotate(-38.2171 88.3961 382.141)' fill='%230F1DED' fill-opacity='0.25'/%3E%3C/g%3E%3Cdefs%3E%3Cfilter id='filter0_f_272_28578' x='-186.359' y='-192.266' width='1036.81' height='991.529' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='BackgroundImageFix' result='shape'/%3E%3CfeGaussianBlur stdDeviation='150' result='effect1_foregroundBlur_272_28578'/%3E%3C/filter%3E%3Cfilter id='filter1_f_272_28578' x='-517.056' y='-165.115' width='1210.9' height='1094.51' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='BackgroundImageFix' result='shape'/%3E%3CfeGaussianBlur stdDeviation='150' result='effect1_foregroundBlur_272_28578'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: '100% 100%',
    backgroundColor: 'white',
  };

  if (messages.length === 0) {
    return (
      <div 
        className="flex-1 flex flex-col items-center justify-center text-center p-8 text-gray-500 border-t border-[#EDEBE9]"
        style={backgroundStyle}
      >
        <div className="w-12 h-12 mb-4 flex items-center justify-center">
          <CopilotIcon className="w-12 h-12" />
        </div>
        <p className="text-lg font-medium text-gray-700">How can I help you today?</p>
      </div>
    );
  }

  return (
    <div 
      className="flex-1 overflow-y-auto p-4 space-y-6 border-t border-[#EDEBE9]"
      style={backgroundStyle}
    >
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={cn(
            "flex gap-3",
            msg.authorRole === "User" ? "flex-row-reverse" : "flex-row"
          )}
        >
          {msg.authorRole === "User" ? (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'rgba(79, 107, 237, 1)' }}
            >
              <User className="w-5 h-5 text-white" />
            </div>
          ) : (
            <div className="w-8 h-8 flex items-center justify-center shrink-0">
              <CopilotIcon className="w-8 h-8" />
            </div>
          )}
          <div
            className={cn(
              "p-3 max-w-[80%] prose prose-sm break-words",
              msg.authorRole === "User"
                ? "rounded-[18px_2px_18px_18px] bg-white shadow-[0px_1.6px_3.6px_0px_rgba(0,0,0,0.13),0px_0.3px_0.9px_0px_rgba(0,0,0,0.1)]"
                : "rounded-[2px_18px_18px_18px] bg-[#D8E3FF] shadow-[0px_1.6px_3.6px_0px_rgba(0,0,0,0.13),0px_0.3px_0.9px_0px_rgba(0,0,0,0.1)]"
            )}
          >
            <ReactMarkdown>{msg.text}</ReactMarkdown>
          </div>
        </div>
      ))}
      {isLoading && (
        <div className="flex gap-3">
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <CopilotIcon className="w-8 h-8" />
          </div>
          <div className="bg-[#D8E3FF] rounded-[2px_18px_18px_18px] p-4 flex items-center gap-1 shadow-[0px_1.6px_3.6px_0px_rgba(0,0,0,0.13),0px_0.3px_0.9px_0px_rgba(0,0,0,0.1)]">
            <div className="w-2 h-2 bg-[#3448AE]/50 rounded-full animate-bounce [animation-delay:-0.3s]" />
            <div className="w-2 h-2 bg-[#3448AE]/50 rounded-full animate-bounce [animation-delay:-0.15s]" />
            <div className="w-2 h-2 bg-[#3448AE]/50 rounded-full animate-bounce" />
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
